import React, { useEffect, useState, useRef } from 'react'
import Api from '../../../../_helpers/Api'
import Skeleton from 'react-loading-skeleton'
import '../../../../assets/css/partials/TodosStatsPerEmployee.css'
import { trans } from '../../../../_providers/Translation'
import GroupedBarChart from '../../../partials/GroupedBarChart'
import { getPaletteColor } from '../../../partials/chartTheme'
import { Button } from '@mui/material'
import CompactFilterLayout from '@/components/partials/CompactFilterLayout'
import DateRange from '@/components/partials/DateRange'
import filterImage from '@/assets/img/icons/filter.svg'

const INITIAL_FILTERS = {
    date_from: null,
    date_to: null,
}

export default function TodosStatsPerEmployee({
    endpoint = 'dashboard/todos-stats-per-employee',
    title = 'Бележки'
}) {
    const [state, setState] = useState({ loading: true, data: null })
    const [filters, setFilters] = useState(INITIAL_FILTERS)

    const compactFilterLayoutRef = useRef(null)
    const fetchDebounceRef = useRef(null)

    const statusColors = {
        awaiting: getPaletteColor(0).color1,
        completed: getPaletteColor(1).color1,
        fail: getPaletteColor(2).color1,
        average_complete_time: getPaletteColor(0).color1,
    }

    const statusLabels = {
        awaiting: trans('tabs.waiting'),
        completed: trans('random.completedShort'),
        fail: trans('random.notCompletedShort'),
        average_complete_time: trans('tables.average_complete_time_short'),
    }

    useEffect(() => {
        clearTimeout(fetchDebounceRef.current)
        fetchDebounceRef.current = setTimeout(() => {
            setState(prev => ({ ...prev, loading: true }))
            Api.get(`${endpoint}?date_from=${filters.date_from || ''}&date_to=${filters.date_to || ''}`)
                .then(res => {
                    const payload = res?.data ?? res
                    setState({ loading: false, data: payload })
                })
                .catch(() => {
                    setState({ loading: false, data: null })
                })
        }, 300)
        return () => clearTimeout(fetchDebounceRef.current)
    }, [endpoint, filters])

    function handleFilterChange(name, value) {
        setFilters(prev => ({ ...prev, [name]: value }))
    }

    function getAppliedFiltersCount() {
        return filters.date_from || filters.date_to ? 1 : 0
    }

    const employeeData = Array.isArray(state.data) ? state.data : []

    const labels = employeeData.map(emp => emp.employee)

    const series = [
        {
            name: statusLabels.awaiting,
            color1: getPaletteColor(0).color1,
            color2: getPaletteColor(0).color2,
            data: employeeData.map(emp => emp.awaiting || 0),
        },
        {
            name: statusLabels.completed,
            color1: getPaletteColor(1).color1,
            color2: getPaletteColor(1).color2,
            data: employeeData.map(emp => emp.completed || 0),
        },
        {
            name: statusLabels.fail,
            color1: getPaletteColor(2).color1,
            color2: getPaletteColor(2).color2,
            data: employeeData.map(emp => emp.fail || 0),
        },
        {
            name: trans('tables.average_complete_time'),
            color1: getPaletteColor(0).color1,
            color2: getPaletteColor(0).color2,
            data: employeeData.map(emp => emp.average_complete_time || 0),
            valueFormatter: (value) => `${value} ${trans('random.min')}`,
        },
    ]

    return (
        <div className="todos-stats-chart">
            <div className="head">
                <h3>{title}</h3>
                <div className="right">
                    <ul>
                        {Object.entries(statusLabels).map(([status, label]) => (
                            <li key={status}>
                                <span style={{ backgroundColor: statusColors[status] }} />
                                {label}
                            </li>
                        ))}
                    </ul>
                    <Button onClick={(e) => compactFilterLayoutRef.current.open(e)}>
                        <img src={filterImage} alt="Filter" />
                        {getAppliedFiltersCount() ?
                            <i>
                                {getAppliedFiltersCount()}
                            </i>
                            :
                            <></>
                        }
                    </Button>
                    <CompactFilterLayout
                        ref={compactFilterLayoutRef}
                        appliedFiltersCount={getAppliedFiltersCount()}
                        onClearClick={() => setFilters(INITIAL_FILTERS)}
                    >
                        <div className='row'>
                            <div className='col'>
                                <DateRange
                                    startDate={filters.date_from || ''}
                                    endDate={filters.date_to || ''}
                                    onStartDateChange={(date) => handleFilterChange('date_from', date)}
                                    onEndDateChange={(date) => handleFilterChange('date_to', date)}
                                />
                            </div>
                        </div>
                    </CompactFilterLayout>
                </div>
            </div>
            <hr />
            {state.loading
                ? <Skeleton height={360} count={1} />
                : <GroupedBarChart labels={labels} series={series} roundedBottom />
            }
        </div>
    )
}
